<template>
    <Detail v-if="data" :logo-on-click="logoOnClick">
        <template v-slot:header>
            <div class="row no-gutters">
                <div class="col col-8">
                    <h2>
                        {{ data.title }}
                        <span class="badge rounded-pill badge-risk" :class="['badge-risk-' + versionRiskLevel(data, version)]"
                            v-b-tooltip.hover :title="hoverTitle(data, version)">
                            {{ $t("risk.levelFull." + versionRiskLevel(data, version)) }}
                        </span>
                    </h2>
                </div>
                <div class="col col-4 text-end">
                    <h2 style="font-weight: normal;">{{ $filters.price(data.price) }}</h2>
                </div>
            </div>
        </template>

        <template #back>
            <router-link :to="{ name: 'tenders', params: { version: version, hash: hash } }" custom v-slot="{ navigate }">
                <button @click="navigate" @keypress.enter="navigate" class="btn btn-inline text-nowrap d-flex align-items-center">
                    <b-icon icon="arrow-left" />&nbsp;{{ $t("tenderDetail.nav.tendersList") }}
                </button>
            </router-link>
        </template>

        <template #breadcrumbs>
            <div class="ellipsis">
                <router-link :to="{ name: version, params: { hash: hash } }">{{ data.analysis_name }}</router-link>
                &nbsp;/&nbsp;
                <router-link :to="{ name: 'tenders', params: { version: version, hash: hash } }">
                    {{ $t("overview.tenders.title")}}
                </router-link>
                &nbsp;/&nbsp;
                {{ data.title }}
            </div>
        </template>

        <template v-slot:header_small>
            <div class="row no-gutters">
                <div class="col col-12">
                    <h2>
                        {{ data.title }}
                        <span class="badge rounded-pill badge-risk"
                            :class="['badge-risk-' + versionRiskLevel(data, version)]">
                            {{ $t("risk.levelFull." + versionRiskLevel(data, version)) }}
                        </span>
                    </h2>
                </div>
            </div>
        </template>

        <template v-slot:menu>
            <Menu :nav="navigation" :current="currentNav" :hash="hash" :version="version"
                @force-nav="(n) => (forceNav = n)" />
        </template>

        <template v-slot:content>
            <section id="overview_section">
                <Summary v-bind="overviewSettings">
                    <template v-slot:value(supplier)="item">
                        <router-link
                            :to="{ name: 'supplier-detail', params: { hash: hash, version: version, slug: item.value.link } }">
                            {{ item.value.name }}
                        </router-link>
                    </template>
                    <template v-slot:value(signatureDate)="item">
                        <template v-if="item.estimated">{{ $filters.formatDate(item.value, "YYYY") }}</template>
                        <template v-else>{{ $filters.formatDate(item.value, "D. M. YYYY") }}</template>
                    </template>
                    <template v-slot:value(price)="item">
                        <span v-if="item.value != null">{{ $filters.price(item.value) }}</span>
                        <span v-else>---</span>
                    </template>
                    <template v-slot:value(paymentsSum)="item">
                        <span>{{ $filters.price(item.value) }}</span>
                    </template>
                    <template v-slot:value(awardUrl)="item">
                        <a :href="item.value.vestnik" v-if="item.value.vestnik" target="_blank"><span>{{ $t("vestnik_url")
                        }}</span><b-icon icon="box-arrow-up-right" font-scale="0.6"
                                style="margin-bottom: 5px;" /></a>
                        <span v-if="item.value.vestnik && item.value.profile">, </span>
                        <a :href="item.value.profile" v-if="item.value.profile" target="_blank"><span>{{ $t("profile_url")
                        }}</span><b-icon icon="box-arrow-up-right" font-scale="0.6"
                                style="margin-bottom: 5px;" /></a>
                                <span v-if="!item.value.vestnik && !item.value.profile">
                                    <span v-if="!item.value.source.includes('www.buyer-profiles.cz') && item.value.award_url == null">
                                        {{ $t("intern_data_url") }}
                                    </span>
                                    <span v-else>{{ item.value.source }}</span>
                                </span>
                    </template>
                </Summary>
            </section>

            <section id="risks_section">

                <h4 class="section-title">
                    {{ $t("tenderDetail.nav.flags") }}
                </h4>

                <EmptySection v-show="!hasFlatFlagsData && !hastenderFlagsData"
                    :message="$t('tenderDetail.flags.nodata')" />

                <div v-show="hasFlatFlagsData">
                    <div class="row align-items-end">
                        <div class="col col-1 flag_header">
                            {{ $t("tenderDetail.flags.header.risk") }}
                        </div>
                        <div class="col col-3 flag_header">
                            {{ $t("tenderDetail.flags.header.group") }}
                        </div>
                        <div class="col flag_header">
                            {{ $t("tenderDetail.flags.header.name") }}
                        </div>
                    </div>

                    <div v-for="(f, i) in flatFlags" :key="'flat-flag-' + i" class="row align-items-center">
                        <div class="col col-12">
                            <SupplierFlatFlag :flag="f" :version="version" />
                        </div>
                    </div>

                </div>

                <div v-if="otherSupplierFlagSettings" class="row other-flags">
                    <div class="col col-12">
                        <SupplierFlatFlag v-bind="otherSupplierFlagSettings" />
                    </div>
                </div>

            </section>

            <section id="concurrency_section" v-if="data.concurrency.length > 0">
                <h4 class="section-title">
                    {{ $t("tenderDetail.nav.concurrency") }}
                </h4>
                <ConcurrencyTable :version="version" :hash="hash" :typeId="data.id" :tenderSlug="slug"
                    v-if="hasConcurrencyData" />
                <EmptySection v-else :message="$t('tenderDetail.concurrency.nodata')" />
            </section>

            <section id="payments_section" v-show="isSectionEnabled('payments')">
                <InvoiceTable :version="version" :hash="hash" :externalData="data" :typeId="data.id" type="tender" />
            </section>

            <section id="nodata_section" v-show="isSectionEnabled('nodata')">
                <h4 class="section-title">
                    {{ $t("tenderDetail.nav.nodata") }}
                </h4>
                <EmptySection :message="$t('tenderDetail.nodata.description', nodataSections)"></EmptySection>
            </section>

        </template>
    </Detail>
</template>

<script>
import Detail from "@/views/layouts/Detail.vue";
import formattersMixin from "@/plugins/formattersMixin.js";
import Menu from "@/components/Menu.vue";
import Summary from "@/components/Summary.vue";
import EmptySection from "@/components/EmptySection.vue";
import SupplierDetailTable from "@/components/SupplierDetailTable.vue";
import SupplierFlatFlag from "@/components/SupplierFlatFlag.vue";
import riskMixin from "@/plugins/riskMixin.js";
import dataTableMixin from "@/plugins/dataTableMixin.js";
import tableSettingsMixin from "@/plugins/tableSettingsMixin.js";
import analysisMixin from "@/plugins/analysisMixin.js";
import InvoiceTable from "@/components/InvoiceTable.vue";
import ConcurrencyTable from "@/components/ConcurrencyTable.vue";

export default {
    props: {
        slug: { required: true },
        version: { required: true },
        hash: { required: true }
    },
    name: "supplier-detail",
    components: {
        Detail,
        Menu,
        EmptySection,
        SupplierDetailTable,
        SupplierFlatFlag,
        Summary,
        InvoiceTable,
        ConcurrencyTable
    },
    mixins: [formattersMixin, riskMixin, dataTableMixin, tableSettingsMixin, analysisMixin],
    created: function () {
        this.init();
    },
    data: function () {
        return {
            data: null,
            nav: {
                overview: {
                    scrollTo: {
                        el: "#overview_section",
                        offset: -200,
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("tenderDetail.nav.overview")
                },
                risks: {
                    scrollTo: {
                        el: "#risks_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    isVisible: () => true,
                    label: this.$t("tenderDetail.nav.risks")
                },
                concurrency: {
                    scrollTo: {
                        el: "#concurrency_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("tenderDetail.nav.concurrency"),
                    isVisible: () => { return this.data.concurrency.length > 0 },
                    nodataSection: true
                },
                payments: {
                    scrollTo: {
                        el: "#payments_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("supplierDetail.nav.payments"),
                    isVisible: () => this.data.invoices_count > 0 || this.data.invoices_count_add_info > 0,
                    requiredVersion: "full"
                },
                nodata: {
                    scrollTo: {
                        el: "#nodata_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("tenderDetail.nav.nodata"),
                    isVisible: () => false
                }
            },
            currentNav: "overview",
            forceNav: null,
            flags: [],
            detailsLoaded: false,
            overviewSettings: {
                title: "Přehled",
                description: "Základní informace o zakázce - datum podepsání smlouvy, vysoutěžená a zaplacená cena a zdroj infromací o zakázce.",
                data: []
            },
            concurrencyTenderTableSettings: {
                isBusy: false,
                initialized: false,
                selectedSortOptionValue: 0,
                filter: {},
                items: [],
                tbodyTrClass: (item) => (item && item.is_supplier ? "clickable" : ""),
                fields: [
                    {
                        key: "name",
                        label: this.$t("tenderDetail.concurrency.table.head.name"),
                        thClass: "text-start text-nowrap text-xl-nowrap",
                        class: "text-start",
                        isActive: true
                    },
                    {
                        key: "flags",
                        label: this.$t("tenderDetail.concurrency.table.head.flags"),
                        thClass: "text-end",
                        class: "text-end detailsButtonColumn",
                        isActive: true
                    },
                ],
                filterTimeout: null,
                filterExpanded: false,
                orderingExpanded: false,
                fieldsExpanded: false,
                flagsExpanded: false,
            },
            flatFlags: [],
            navigation: {},
            nodata: {},
            tenderFlags: []
        };
    },
    watch: {
        nodata: function (value) {
            if (!Object.keys(value).length) {
                delete this.navigation["nodata"];
            } else {
                this.navigation["nodata"] = this.nav.nodata;
            }
        },
        "$route.params.slug": function () {
            this.data = null
            this.flatFlags = []
            this.navigation = {}
            this.nodata = {}
            this.tenderFlags = []

            this.init()
                .then(() => {
                    this.$refs.payments_data_table.refresh()
                })
        }
    },
    computed: {

        filterDebounce: () => 700,
        nodataSections: function () {
            return {
                sections: Object.values(this.nodata)
                    .map((n) => n.label)
                    .join(", ")
            };
        },
        hasFlatFlagsData: function () {
            return this.flatFlags.length;
        },
        hastenderFlagsData: function () {
            return this.tenderFlags.length;
        },
        hasConcurrencyData: function () {
            return this.data.concurrency.length > 0;
        },
        otherSupplierFlagSettings: function () {
            if (!this.tenderFlags.length) {
                return null
            }

            var worst = this.tenderFlags.reduce((max, flag) => {
                return max.risk > flag.risk ? max : flag;
            })

            let settings = {
                flag: {
                    flag_type_group_name: this.$t('tenderDetail.flags.other.group'),
                    flag_type_name: this.$t('tenderDetail.flags.other.name', { count: this.tenderFlags.length }),
                    description: this.$t('tenderDetail.flags.other.desc'),
                    risk: worst.risk
                },
                version: this.version
            }

            settings.flag[`risk_level_${this.version}`] = worst[`risk_level_${this.version}`]

            return settings
        }
    },
    methods: {
        init: function () {
            return new Promise((resolve, reject) => {
                this.loadData()
                    .then(() => {
                        this.overviewSettings.data = this.buildOverviewData();

                        this.loadFlatFlagsSupplier();
                        this.loadFlatFlagsTender();
                        this.loadtenderFlagsSupplier();

                        this.buildNavigation();

                        window.removeEventListener("scroll", this.onScroll);
                        window.addEventListener("scroll", this.onScroll);

                        this.$root.$emit("set-page-title", this.$t("titleDetail", { name: this.data.title }))

                        resolve()
                    })
                    .catch(e => reject(e))
            })
        },
        onScroll: function () {
            if (this.forceNav) {
                return;
            }

            Object.keys(this.navigation).forEach((k) => {
                var n = this.navigation[k];

                if (n.scrollTo) {
                    var el = document.querySelector(n.scrollTo.el);
                    if (el) {
                        var top = el.getBoundingClientRect().top;
                        if (top > 0 && top <= 100) {
                            this.currentNav = k;
                        }
                    }
                }
            });
        },
        loadData: function () {
            return new Promise((resolve, reject) => {
                var self = this;

                var params = {};

                params[`${this.version}_hash`] = this.hash;
                params["bidders"] = true;

                this.$store.getters.api
                    .get("tender/" + this.slug, { params: params })
                    .then((response) => {
                        var data = response.data;
                        self.data = data;
                        self.concurrencyTenderTableSettings.items = data.concurrency;
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                        self.data = null;
                    });
            });
        },
        loadFlatFlagsTender: function () {
            var params = {
                format: "json",
                tender: this.data.id,
                flag_level: "tender",
                order_by: "-risk",
                bidders_is_null: true,
                limit: 0
            };

            params[`${this.version}_hash`] = this.hash;

            return this.$store.getters.api
                .get("flag/", { params: params })
                .then((response) => {
                    response.data.data.forEach(
                        (obj) => { this.flatFlags.push(obj); }
                    );
                    this.flatFlags.sort(function compareFn(firstEl, secondEl) {
                        var fEl = parseFloat(firstEl.risk);
                        var sEl = parseFloat(secondEl.risk);
                        if (fEl > sEl) {
                            return -1;
                        }
                        if (fEl < sEl) {
                            return 1;
                        }
                        return 0;
                    });
                })
                .catch((e) => {
                    console.dir(e);
                    this.flatFlags = [];
                });
        },
        loadFlatFlagsSupplier: function () {
            var params = {
                format: "json",
                supplier: this.data.supplier_tender.id,
                flag_level: "supplier,analysis_supplier",
                order_by: "-risk",
                limit: 0
            };

            params[`${this.version}_hash`] = this.hash;

            return this.$store.getters.api
                .get("flag/", { params: params })
                .then((response) => {
                    response.data.data.forEach(
                        (obj) => { this.flatFlags.push(obj); }
                    );
                    this.flatFlags.sort(function compareFn(firstEl, secondEl) {
                        var fEl = parseFloat(firstEl.risk);
                        var sEl = parseFloat(secondEl.risk);
                        if (fEl > sEl) {
                            return -1;
                        }
                        if (fEl < sEl) {
                            return 1;
                        }
                        return 0;
                    });
                })
                .catch((e) => {
                    console.dir(e);
                    this.flatFlags = [];
                });
        },
        loadtenderFlagsSupplier: function () {
            var params = {
                format: "json",
                tender: this.data.id,
                flag_level: "tender",
                bidders_is_null: false,
                limit: 0
            };

            params[`${this.version}_hash`] = this.hash;

            return this.$store.getters.api
                .get("flag/", { params: params })
                .then((response) => {
                    this.tenderFlags = response.data.data
                })
                .catch((e) => {
                    console.log(e)
                    this.tenderFlags = []
                });
        },
        onNav: function (value) {
            this.$scrollTo(this.nav[value.section].scrollTo.el, "body", this.nav[value.section].scrollTo);
        },
        isSectionEnabled: function (section) {
            return Object.prototype.hasOwnProperty.call(this.navigation, section);
        },
        buildNavigation: function () {
            this.navigation = {};
            this.nodata = {};
            Object.keys(this.nav).forEach((n) => {
                let nav = { ...this.nav[n] };

                var verified = true
                if (!this.verifyVersion(this.version, nav.requiredVersion)) {
                    delete nav["scrollTo"];
                    delete nav["to"];
                    verified = false
                }

                if (!Object.prototype.hasOwnProperty.call(nav, "isVisible") || nav.isVisible()) {
                    this.navigation[n] = nav;
                } else if (nav.nodataSection && verified) {
                    this.nodata[n] = nav;
                }
            });
        },
        buildOverviewData: function () {
            return {
                supplier: {
                    label: this.$t("tenderDetail.overviewSummary.supplier"),
                    value: this.data.supplier_tender
                },
                signatureDate: {
                    label: this.$t("tenderDetail.overviewSummary.signatureDate"),
                    value: this.data.signature_date,
                    estimated: this.data.estimated_signature_date
                },
                price: {
                    label: this.$t("tenderDetail.overviewSummary.price", this.data.merged_lots_count || 0),
                    value: this.data.price
                },
                paymentsSum: {
                    label: this.$t("tenderDetail.overviewSummary.paymentsSum"),
                    value: this.data.payments_sum,
                    isVisible: () => this.data.payments_sum > 0
                },
                awardUrl: {
                    label: this.$t("tenderDetail.overviewSummary.awardUrl"),
                    value: this.data.award_url
                }
            };
        },
        logoOnClick: function () {
            this.$router.push({ name: this.version, params: { hash: this.hash } })
        },
        hoverTitle: function (data, version) {
            var level = this.versionRiskLevel(data, version);
            return this.$t(`risk.levelFull.${level}`) + (level != 'no' ? ` ${this.formatPercentage(data.risk, 2)}` : '')
        }
    }
};
</script>

<style lang="scss">
@use "@/scss/custom";

#menu {
    margin-bottom: 50px;

    button {
        text-align: left;
        background-color: white;
        padding: 0.6rem 1.5rem;
        padding-right: 0;

        &.active {
            background-color: custom.$background-light;
        }

        &:hover {
            background-color: custom.$light;
        }

        &[disabled] {
            background-color: transparent;
        }

        .premium_label {
            float: right;
        }
    }
}


.section-title {
    font-size: 20px;
    font-weight: 700;
    color: custom.$body-color;
    margin-bottom: 20px;
}

#payments_section {
    .b-table-has-details td {
        border-bottom: none;
    }

    .b-table-details {
        background-color: initial;

        td {
            padding-top: 0;
        }
    }
}

.empty-section~.other-flags {
    margin-top: 10px;
}

.overview_description {
    width: 80%;
}

.detailsButtonColumn {
    width: 60px;
    height: 70px !important;
    padding: 0px !important;
}
</style>
